import { useEffect, useRef, useState } from 'react';
import {
  Copy,
  PencilLine,
  RotateCw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { cn } from '@/shared/cn';
import { CitationChip } from '@/design-system/CitationChip';
import { Commentable } from '@/shared/comments/Commentable';
import { useDrawer } from '@/app/providers';
import { getCitationSource } from '@/versions/v1/shared/citation-registry';
import type { ChatMessage, ChatMessagePart } from '@/versions/v1/shared/chat-types';
import { useChat } from '@/versions/v1/shared/ChatContext';
// fork(oem): the OEM+ local engine answers in markdown and cites datasets.
import { AssistantMarkdown } from '@/versions/v1/shared/assistant/markdown';
import { useOemSourceChip } from '@/versions/v1/shared/assistant/useOemSourceChip';
// fork(L3): a generated report's steps and card.
import { ReportChatCard } from '@/versions/v1/chrome-oem/ReportChatCard';
// fork(oem v1.8): thumbs up / down acknowledge with a toast.
import { useToast } from '@/versions/v1/shared/toast/ToastContext';
import { CompactConversationNotice } from './ThreadNoticeRail';

/** fork(oem v1.8): the toast a thumbs up / down raises. */
const FEEDBACK_TOAST = 'Thanks — noted for the reviewers';

/** fork(oem v1.8): a pressed thumb reads in the accent, not the idle grey. */
const FEEDBACK_PRESSED_CLASS =
  'text-[var(--accent)] hover:text-[var(--accent)] dark:text-[var(--accent)] dark:hover:text-[var(--accent)]';

const USER_ACTION_BUTTON_CLASS =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-[color-mix(in_srgb,var(--color-zinc-900)_5%,transparent)] hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-white/5 dark:hover:text-zinc-100';

export function ChatMessages() {
  const {
    messages,
    compactedEarlierCount,
    thinking,
    thinkingLabel,
    setDraft,
    setEditingMessageId,
    setEditDraft,
    resend,
  } = useChat();
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const visibleMessages =
    compactedEarlierCount > 0 ? messages.slice(compactedEarlierCount) : messages;

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, thinking, thinkingLabel, compactedEarlierCount]);

  return (
    <Commentable name="Messages">
      <div
        ref={bodyRef}
        className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto"
      >
        <div className="mx-auto w-full max-w-[var(--thread-max-width)] px-4 py-3">
          <CompactConversationNotice count={compactedEarlierCount} />
          {visibleMessages.map((m) =>
            m.who === 'user' ? (
              <UserMessage
                key={m.id}
                message={m}
                onEdit={() => {
                  setEditingMessageId(m.id);
                  setEditDraft(m.body);
                  setDraft('');
                }}
                onResend={() => resend(m.body)}
              />
            ) : (
              <AssistantMessage key={m.id} message={m} />
            ),
          )}
          {thinking ? <ThinkingLine label={thinkingLabel} /> : null}
        </div>
      </div>
    </Commentable>
  );
}

function UserMessage({
  message,
  onEdit,
  onResend,
}: {
  message: ChatMessage;
  onEdit: () => void;
  onResend: () => void;
}) {
  const copy = () => {
    void navigator.clipboard.writeText(message.body);
  };

  return (
    <div
      className={cn(
        'group/message mx-auto w-full max-w-[var(--thread-max-width)] px-0 pt-4 pb-9',
        'grid grid-cols-[auto_minmax(0,1fr)] gap-x-4',
        '[animation:message-in_320ms_cubic-bezier(0.22,0.61,0.36,1)_both] motion-reduce:animate-none',
      )}
    >
      <div className="col-start-2 flex justify-end">
        <div className="relative flex max-w-[80%] flex-col items-end gap-2">
          <div className="w-full max-w-[80%] rounded-2xl border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] px-3 py-2.5 backdrop-blur-md text-sm text-zinc-900 dark:text-zinc-100">
            {message.body}
          </div>
          <div className="absolute right-0 top-full mt-1 flex w-max items-center gap-1 whitespace-nowrap opacity-0 transition-opacity group-hover/message:opacity-100">
            <span className="whitespace-nowrap text-xs tabular-nums text-zinc-400 dark:text-zinc-500">
              {message.time}
            </span>
            <button
              type="button"
              className={USER_ACTION_BUTTON_CLASS}
              title="Copy"
              aria-label="Copy"
              onClick={copy}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className={USER_ACTION_BUTTON_CLASS}
              title="Edit message"
              aria-label="Edit message"
              onClick={onEdit}
            >
              <PencilLine className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className={USER_ACTION_BUTTON_CLASS}
              title="Resend this prompt"
              aria-label="Resend this prompt"
              onClick={onResend}
            >
              <RotateCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// fork(oem v1.8): exported so the welcome state can show the morning briefing.
export function AssistantMessage({ message }: { message: ChatMessage }) {
  const { open: openDrawer } = useDrawer();
  // fork(oem): a source chip resolves to a table slice, a doc or a route.
  const onSourceChip = useOemSourceChip();
  // fork(oem v1.8): thumbs up / down hold a pressed state and say thanks.
  const { show } = useToast();
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const rate = (value: 'up' | 'down') => {
    const next = feedback === value ? null : value;
    setFeedback(next);
    if (next) show(FEEDBACK_TOAST);
  };

  const copy = () => {
    void navigator.clipboard.writeText(message.body);
  };

  return (
    <div
      className={cn(
        'group/message mx-auto w-full max-w-[var(--thread-max-width)] px-0 py-4',
        '[animation:message-in_320ms_cubic-bezier(0.22,0.61,0.36,1)_both] motion-reduce:animate-none',
      )}
    >
      <div className="min-w-0">
        {message.parts?.length ? (
          <div className="space-y-3">
            {message.parts.map((part, index) => (
              <AssistantPart key={`${message.id}-${index}`} part={part} />
            ))}
          </div>
        ) : (
          <div className="text-sm text-zinc-900 dark:text-zinc-100">{message.body}</div>
        )}
        {message.citations && message.citations.length > 0 ? (
          <div className="mt-2">
            {message.citations.map((c, i) => {
              const id = c.label.split(/\s+§/)[0];
              const source = getCitationSource(id);
              return (
                <CitationChip
                  key={i}
                  label={c.label}
                  onClick={() => {
                    // fork(oem): OEM+ labels name datasets, not DOC-00n ids.
                    if (!source) {
                      onSourceChip(c.label);
                      return;
                    }
                    openDrawer('citation', {
                      kicker: 'CITATION',
                      title: source.title,
                      data: { citationId: id },
                    });
                  }}
                />
              );
            })}
          </div>
        ) : null}
        <div
          className={cn(
            'mt-1 flex items-center gap-1 transition-opacity group-hover/message:opacity-100',
            // fork(oem v1.8): a rated message keeps its row visible.
            feedback ? 'opacity-100' : 'opacity-0',
          )}
        >
          <button
            type="button"
            className={USER_ACTION_BUTTON_CLASS}
            title="Copy"
            aria-label="Copy"
            onClick={copy}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={cn(USER_ACTION_BUTTON_CLASS, feedback === 'up' && FEEDBACK_PRESSED_CLASS)}
            title="Good response"
            aria-label="Good response"
            aria-pressed={feedback === 'up'}
            onClick={() => rate('up')}
          >
            <ThumbsUp className="h-3.5 w-3.5" fill={feedback === 'up' ? 'currentColor' : 'none'} />
          </button>
          <button
            type="button"
            className={cn(USER_ACTION_BUTTON_CLASS, feedback === 'down' && FEEDBACK_PRESSED_CLASS)}
            title="Bad response"
            aria-label="Bad response"
            aria-pressed={feedback === 'down'}
            onClick={() => rate('down')}
          >
            <ThumbsDown className="h-3.5 w-3.5" fill={feedback === 'down' ? 'currentColor' : 'none'} />
          </button>
          {/* fork(oem v1.8): bot turns carry their time too (the briefing's 06:05). */}
          <span className="ml-1 whitespace-nowrap text-xs tabular-nums text-zinc-400 dark:text-zinc-500">
            {message.time}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * fork(oem v1.8): the demo's `#thinking` line — "Thinking…", then
 * "Working… a → b" as the local engine reports each tool.
 */
function ThinkingLine({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-start gap-2 py-2 text-sm italic text-zinc-400 dark:text-zinc-500"
    >
      <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin" aria-hidden />
      <span className="min-w-0 break-words">{label}</span>
    </div>
  );
}

function AssistantPart({ part }: { part: ChatMessagePart }) {
  // fork(oem): a roadmap surface, shown as the card it will become.
  if (part.type === 'notice') {
    return (
      <div className="rounded-xl border border-dashed border-[var(--glass-border-strong)] bg-[var(--glass-surface-soft)] p-4 opacity-80">
        <div className="text-xs uppercase tracking-wide text-muted">{part.kind}</div>
        <div className="mt-1 font-medium text-zinc-900 dark:text-zinc-100">{part.title}</div>
        <div className="text-xs text-muted">{part.subtitle}</div>
        <button
          type="button"
          disabled
          className="mt-3 cursor-not-allowed rounded-md border border-[var(--hairline)] px-2.5 py-1 text-xs text-muted"
        >
          Download — on the roadmap
        </button>
      </div>
    );
  }

  if (part.type === 'actions') {
    return <PartActions actions={part.actions} />;
  }

  // fork(L3): the demo's `reportProgress` + `reportCard`.
  if (part.type === 'report') {
    return <ReportChatCard part={part} />;
  }

  if (part.type === 'tool') {
    return (
      <div className="rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-3 py-2 text-sm">
        <div className="font-medium text-zinc-900 dark:text-zinc-100">
          {part.verb}
        </div>
        <div className="text-muted">{part.preview}</div>
        {part.detail ? <div className="mt-1 text-xs text-muted">{part.detail}</div> : null}
      </div>
    );
  }

  if (part.type === 'artifact') {
    return (
      <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 shadow-sm">
        <div className="text-xs uppercase tracking-wide text-muted">{part.kind}</div>
        <div className="mt-1 font-medium text-zinc-900 dark:text-zinc-100">
          {part.title}
        </div>
      </div>
    );
  }

  // fork(oem): markdown parts render as markdown, not de-asterisked plain text.
  return <AssistantMarkdown source={part.content} />;
}

/** fork(oem): the follow-up buttons an answer can offer. */
function PartActions({ actions }: { actions: { label: string; prompt: string }[] }) {
  const { send } = useChat();
  return (
    <div className="flex flex-wrap gap-1.5">
      {actions.map((a) => (
        <button
          key={a.prompt}
          type="button"
          onClick={() => send(a.prompt)}
          className="rounded-full border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-3 py-1 text-xs text-zinc-600 transition-colors hover:border-[var(--glass-border-strong)] hover:bg-[var(--glass-surface)] hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100"
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}
